"use client";

import { useQueryClient } from "@tanstack/react-query";
import {
  Ban,
  ChevronRight,
  Flag,
  MoreHorizontal,
  Share,
  ShieldCheck,
  UserMinus,
  VolumeX,
} from "lucide-react";
import { useRouter } from "next/navigation";
import {
  type ReactNode,
  useEffect,
  useMemo,
  useState,
  useTransition,
} from "react";
import { toast } from "sonner";
import {
  blockBuddy,
  endBuddyConnection,
  setBuddyMute,
  unblockBuddy,
} from "@/actions/buddy";
import { reportUser } from "@/actions/user-report";
import {
  GLASS_DANGER_BUTTON,
  GLASS_DANGER_TEXT,
  GLASS_FIELD,
  GLASS_GROUP,
  GLASS_GROUP_DIVIDE,
  GLASS_ICON,
  GLASS_ICON_TONE,
  GLASS_MUTED_TEXT,
  GLASS_PRIMARY_BUTTON,
  GLASS_ROW,
  GLASS_SHEET,
  GLASS_SHEET_OVERLAY,
  GLASS_TILE,
} from "@/components/share/liquid-glass-sheet";
import { Button } from "@/components/ui/button";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { invalidateBuddyRelationships } from "@/lib/buddy/relationship-cache";
import { cn } from "@/lib/utils";

type ManageAction = "block" | "mute" | "unblock" | "unfriend" | "report";

const reportReasons = [
  { value: "harassment", label: "Harassment" },
  { value: "spam", label: "Spam" },
  { value: "safety", label: "Safety concern" },
  { value: "inappropriate_content", label: "Inappropriate content" },
  { value: "fake_profile", label: "Fake profile" },
  { value: "other", label: "Other" },
] as const;

type ReportReason = (typeof reportReasons)[number]["value"];

export type BuddyManageDrawerProps = {
  targetUserId: string;
  targetName: string | null;
  isBuddy: boolean;
  blockedByMe: boolean;
  mutePosts: boolean;
  muteStories: boolean;
  /**
   * Shows a "Share profile" row. The caller owns the share sheet so it can
   * open after this one closes, rather than stacked inside it.
   */
  onShare?: () => void;
  /** @deprecated Pass `onShare`; a custom node can't match the row styling. */
  shareAction?: ReactNode;
  onActionComplete?: (action: ManageAction) => void;
};

export function BuddyManageDrawer({
  targetUserId,
  targetName,
  isBuddy,
  blockedByMe,
  mutePosts: initialMutePosts,
  muteStories: initialMuteStories,
  onShare,
  shareAction,
  onActionComplete,
}: BuddyManageDrawerProps) {
  const router = useRouter();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<ManageAction | null>(null);
  const [reportReason, setReportReason] = useState<ReportReason>("other");
  const [reportDetails, setReportDetails] = useState("");
  const [mutePosts, setMutePosts] = useState(initialMutePosts);
  const [muteStories, setMuteStories] = useState(initialMuteStories);
  const [isPending, startTransition] = useTransition();

  const displayName = targetName?.split(" ")[0] || "this user";

  useEffect(() => {
    if (!isOpen) return;
    setMutePosts(initialMutePosts);
    setMuteStories(initialMuteStories);
  }, [initialMutePosts, initialMuteStories, isOpen]);

  const confirmCopy = useMemo(() => {
    switch (confirmAction) {
      case "unfriend":
        return {
          title: `Remove ${displayName}?`,
          description:
            "You will both lose the ability to message each other until you connect again.",
          confirmLabel: "Remove",
          icon: UserMinus,
          variant: "destructive" as const,
        };
      case "block":
        return {
          title: `Block ${displayName}?`,
          description: "They won't be able to message you or see your profile.",
          confirmLabel: "Block",
          icon: Ban,
          variant: "destructive" as const,
        };
      case "unblock":
        return {
          title: `Unblock ${displayName}?`,
          description:
            "They will be able to see your profile and request to connect.",
          confirmLabel: "Unblock",
          icon: ShieldCheck,
          variant: "default" as const,
        };
      default:
        return null;
    }
  }, [confirmAction, displayName]);

  const close = () => {
    setIsOpen(false);
    setTimeout(() => {
      setConfirmAction(null);
      setReportReason("other");
      setReportDetails("");
    }, 300);
  };

  const onConfirm = () => {
    if (!confirmAction || confirmAction === "report") return;

    startTransition(async () => {
      const result =
        confirmAction === "unfriend"
          ? await endBuddyConnection(targetUserId)
          : confirmAction === "block"
            ? await blockBuddy(targetUserId)
            : await unblockBuddy(targetUserId);

      if (!result.ok) {
        toast.error(result.message ?? "Something went wrong");
        return;
      }

      toast.success(
        confirmAction === "unfriend"
          ? "Removed from buddies"
          : confirmAction === "block"
            ? "Blocked successfully"
            : "Unblocked successfully",
      );

      invalidateBuddyRelationships(queryClient, targetUserId);

      // Refresh any screens that might surface this user's identity.
      // (Feed, members lists, chats, etc.) This makes the UI update immediately
      // even if a query is still "fresh" per staleTime.
      queryClient.invalidateQueries({ queryKey: ["posts"] });
      queryClient.invalidateQueries({ queryKey: ["activityPosts"] });
      queryClient.invalidateQueries({ queryKey: ["activitySessions"] });
      queryClient.invalidateQueries({ queryKey: ["hiive"] });
      queryClient.invalidateQueries({ queryKey: ["event"] });
      queryClient.invalidateQueries({ queryKey: ["userChats"] });
      queryClient.invalidateQueries({ queryKey: ["global-search"] });

      close();
      onActionComplete?.(confirmAction);

      if (confirmAction === "block") {
        router.push("/discover");
        return;
      }

      router.refresh();
    });
  };

  const onSubmitReport = () => {
    startTransition(async () => {
      const result = await reportUser({
        targetUserId,
        reason: reportReason,
        details: reportDetails,
      });

      if (!result.ok) {
        toast.error(result.message ?? "Unable to submit report");
        return;
      }

      toast.success(
        result.data.duplicate
          ? "You already have an open report for this user"
          : "Report submitted",
      );
      close();
      onActionComplete?.("report");
    });
  };

  const onSaveMute = () => {
    startTransition(async () => {
      const result = await setBuddyMute({
        targetUserId,
        mutePosts,
        muteStories,
      });
      if (!result.ok) {
        toast.error(result.message ?? "Unable to update mute settings");
        return;
      }

      toast.success(
        mutePosts || muteStories ? "Mute settings saved" : "Account unmuted",
      );
      queryClient.invalidateQueries({ queryKey: ["posts"] });
      queryClient.invalidateQueries({ queryKey: ["activityPosts"] });
      queryClient.invalidateQueries({ queryKey: ["stories"] });
      queryClient.invalidateQueries({ queryKey: ["public-profile-actions"] });
      close();
      onActionComplete?.("mute");
      router.refresh();
    });
  };

  const firstName = targetName?.split(" ")[0] || null;
  const mutedSummary =
    initialMutePosts && initialMuteStories
      ? "Posts, stories"
      : initialMutePosts
        ? "Posts"
        : initialMuteStories
          ? "Stories"
          : null;

  const handleShare = () => {
    if (!onShare) return;
    // Close this sheet first so Share opens on the page, not stacked on top
    // of a sheet that has nothing left to do.
    setIsOpen(false);
    onShare();
  };

  return (
    <Drawer
      open={isOpen}
      onOpenChange={(open) => {
        if (open) {
          setIsOpen(true);
        } else {
          close();
        }
      }}
    >
      <DrawerTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 rounded-full text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground"
          aria-label="Options"
        >
          <MoreHorizontal className="size-5" />
        </Button>
      </DrawerTrigger>

      <DrawerContent
        className={GLASS_SHEET}
        overlayClassName={GLASS_SHEET_OVERLAY}
      >
        <div className="mx-auto w-full max-w-md px-4 pt-2.5 pb-[max(env(safe-area-inset-bottom),1rem)]">
          {!confirmAction ? (
            <div className="flex flex-col gap-3">
              <DrawerHeader className="p-0 pb-1 text-center md:text-center">
                <DrawerTitle className="truncate text-[17px] font-semibold tracking-tight text-text-primary dark:text-white">
                  {targetName || "Options"}
                </DrawerTitle>
                <DrawerDescription className="sr-only">
                  Share, mute, block or report this profile
                </DrawerDescription>
              </DrawerHeader>

              <div className={cn(GLASS_GROUP, GLASS_GROUP_DIVIDE)}>
                {onShare ? (
                  <ManageRow
                    icon={<Share />}
                    tone="brand"
                    label="Share profile"
                    onClick={handleShare}
                  />
                ) : null}

                {!blockedByMe ? (
                  <ManageRow
                    icon={<VolumeX />}
                    label="Mute"
                    detail={mutedSummary}
                    chevron
                    onClick={() => setConfirmAction("mute")}
                  />
                ) : (
                  <ManageRow
                    icon={<ShieldCheck />}
                    label={firstName ? `Unblock ${firstName}` : "Unblock"}
                    onClick={() => setConfirmAction("unblock")}
                  />
                )}
              </div>

              {/* Older callers hand in their own share control. */}
              {!onShare && shareAction ? shareAction : null}

              <div className={cn(GLASS_GROUP, GLASS_GROUP_DIVIDE)}>
                {isBuddy ? (
                  <ManageRow
                    icon={<UserMinus />}
                    tone="danger"
                    label="Remove buddy"
                    onClick={() => setConfirmAction("unfriend")}
                  />
                ) : null}
                {!blockedByMe ? (
                  <ManageRow
                    icon={<Ban />}
                    tone="danger"
                    label={firstName ? `Block ${firstName}` : "Block"}
                    onClick={() => setConfirmAction("block")}
                  />
                ) : null}
                <ManageRow
                  icon={<Flag />}
                  tone="danger"
                  label={firstName ? `Report ${firstName}` : "Report"}
                  chevron
                  onClick={() => setConfirmAction("report")}
                />
              </div>

              <button
                type="button"
                className={cn(
                  GLASS_TILE,
                  "mt-1 h-[52px] w-full rounded-[20px] text-[15px] font-semibold",
                )}
                onClick={() => setIsOpen(false)}
              >
                Cancel
              </button>
            </div>
          ) : confirmAction === "mute" ? (
            <ManageSubview
              icon={<VolumeX />}
              tone="neutral"
              title={`Mute ${displayName}`}
              description="They won’t be notified. You can still visit their profile and message them."
            >
              <div className={cn(GLASS_GROUP, GLASS_GROUP_DIVIDE)}>
                <MuteToggleRow
                  label="Posts"
                  description="Hide them from your feed"
                  checked={mutePosts}
                  onCheckedChange={setMutePosts}
                />
                <MuteToggleRow
                  label="Stories"
                  description="Hide them from your story tray"
                  checked={muteStories}
                  onCheckedChange={setMuteStories}
                />
              </div>

              <ManageSubviewActions
                primaryLabel={isPending ? "Saving…" : "Save"}
                onPrimary={onSaveMute}
                onBack={() => setConfirmAction(null)}
                isPending={isPending}
              />
            </ManageSubview>
          ) : confirmAction === "report" ? (
            <ManageSubview
              icon={<Flag />}
              tone="danger"
              title={`Report ${displayName}`}
              description="Reports go to the Hiive team for review."
            >
              <div className="flex flex-col gap-2.5">
                <Select
                  value={reportReason}
                  onValueChange={(value) =>
                    setReportReason(value as ReportReason)
                  }
                >
                  <SelectTrigger
                    aria-label="Reason"
                    className={cn(
                      "h-12 w-full rounded-2xl px-4 text-[15px] shadow-none",
                      GLASS_FIELD,
                    )}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {reportReasons.map((reason) => (
                      <SelectItem key={reason.value} value={reason.value}>
                        {reason.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* 16px below md, or iOS zooms the page on focus. */}
                <Textarea
                  value={reportDetails}
                  onChange={(event) => setReportDetails(event.target.value)}
                  placeholder="What happened? (optional)"
                  aria-label="Details"
                  className={cn(
                    "min-h-28 rounded-2xl text-base shadow-none backdrop-blur-none md:text-sm dark:shadow-none",
                    GLASS_FIELD,
                  )}
                />
              </div>

              <ManageSubviewActions
                primaryLabel={isPending ? "Sending…" : "Send report"}
                onPrimary={onSubmitReport}
                onBack={() => setConfirmAction(null)}
                isPending={isPending}
              />
            </ManageSubview>
          ) : (
            <ManageSubview
              icon={confirmCopy?.icon ? <confirmCopy.icon /> : null}
              tone={
                confirmCopy?.variant === "destructive" ? "danger" : "neutral"
              }
              title={confirmCopy?.title ?? ""}
              description={confirmCopy?.description ?? ""}
            >
              <ManageSubviewActions
                primaryLabel={
                  isPending ? "Working…" : (confirmCopy?.confirmLabel ?? "")
                }
                destructive={confirmCopy?.variant === "destructive"}
                onPrimary={onConfirm}
                onBack={() => setConfirmAction(null)}
                isPending={isPending}
              />
            </ManageSubview>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}

type ManageTone = keyof typeof GLASS_ICON_TONE;

function ManageRow({
  icon,
  label,
  detail,
  tone = "neutral",
  chevron = false,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  detail?: string | null;
  tone?: ManageTone;
  chevron?: boolean;
  onClick: () => void;
}) {
  return (
    <button type="button" className={GLASS_ROW} onClick={onClick}>
      <span
        aria-hidden="true"
        className={cn(GLASS_ICON, GLASS_ICON_TONE[tone])}
      >
        {icon}
      </span>
      <span
        className={cn(
          "min-w-0 flex-1 truncate font-medium",
          tone === "danger" && GLASS_DANGER_TEXT,
        )}
      >
        {label}
      </span>
      {detail ? (
        <span className={cn("shrink-0 text-[14px]", GLASS_MUTED_TEXT)}>
          {detail}
        </span>
      ) : null}
      {chevron ? (
        <ChevronRight
          aria-hidden="true"
          className="size-4 shrink-0 text-black/25 dark:text-white/30"
        />
      ) : null}
    </button>
  );
}

function ManageSubview({
  icon,
  tone,
  title,
  description,
  children,
}: {
  icon: ReactNode;
  tone: ManageTone;
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <div className="flex animate-in flex-col gap-5 pt-2 duration-200 ease-out-strong fade-in slide-in-from-bottom-2">
      <div className="flex flex-col items-center text-center">
        <span
          aria-hidden="true"
          className={cn(
            "mb-3 flex size-14 items-center justify-center rounded-[18px] [&_svg]:size-7",
            GLASS_ICON_TONE[tone],
          )}
        >
          {icon}
        </span>
        <h3 className="text-[19px] font-semibold tracking-tight text-text-primary dark:text-white">
          {title}
        </h3>
        <p
          className={cn(
            "mt-1 max-w-[19rem] text-[14px] leading-relaxed",
            GLASS_MUTED_TEXT,
          )}
        >
          {description}
        </p>
      </div>
      {children}
    </div>
  );
}

function ManageSubviewActions({
  primaryLabel,
  onPrimary,
  onBack,
  isPending,
  destructive = false,
}: {
  primaryLabel: string;
  onPrimary: () => void;
  onBack: () => void;
  isPending: boolean;
  destructive?: boolean;
}) {
  return (
    <div className="flex flex-col gap-2">
      <button
        type="button"
        className={destructive ? GLASS_DANGER_BUTTON : GLASS_PRIMARY_BUTTON}
        disabled={isPending}
        onClick={onPrimary}
      >
        {primaryLabel}
      </button>
      <button
        type="button"
        className={cn(
          "h-11 w-full rounded-full text-[15px] font-medium transition-colors duration-150 ease-out-strong active:bg-black/[0.05] disabled:opacity-60 dark:active:bg-white/[0.07]",
          GLASS_MUTED_TEXT,
        )}
        disabled={isPending}
        onClick={onBack}
      >
        Back
      </button>
    </div>
  );
}

function MuteToggleRow({
  label,
  description,
  checked,
  onCheckedChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}) {
  return (
    <div className="flex min-h-[60px] items-center gap-3 px-4 py-2.5">
      <div className="min-w-0 flex-1">
        <div className="text-[15px] font-medium text-text-primary dark:text-white">
          {label}
        </div>
        <p className={cn("mt-0.5 text-[13px]", GLASS_MUTED_TEXT)}>
          {description}
        </p>
      </div>
      <Switch
        checked={checked}
        onCheckedChange={onCheckedChange}
        aria-label={`Mute ${label.toLowerCase()}`}
      />
    </div>
  );
}
